import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi, apiPost, Cargando, ErrorApi, formatU, formatFecha, confirmar } from '../shared/ui.tsx';

interface CardData {
  total: number;
  incompletas: number;
  solicitudes: Array<{ id: string; fecha: string; cliente_nombre: string; salon_nombre: string; faltan: number }>;
}

interface NoShowData {
  total: number;
  criticos: number;
  personas: Array<{ persona_id: string; nombre: string; apellido: string; no_show_count: number }>;
}

interface MargenData {
  total_cobro: number;
  total_pago: number;
  margen: number;
  turnos: number;
}

export function DashboardPage(): ReactNode {
  const alertas = useApi<CardData>('/api/alertas/cobertura');
  const noShow = useApi<NoShowData>('/api/alertas/no-show');
  const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const finMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10);
  const margen = useApi<MargenData>(`/api/reportes/margen?desde=${inicioMes}&hasta=${finMes}`);

  const [notifPermiso, setNotifPermiso] = useState<string>('');
  useEffect(() => {
    if ('Notification' in window) {
      setNotifPermiso(Notification.permission);
      const id = setInterval(() => setNotifPermiso(Notification.permission), 5000);
      return () => clearInterval(id);
    }
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Hola, Fernando 👋</h2>
        <p className="text-sm capitalize text-neutro-400">
          {new Date().toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      <SwitchAsignacionAutomatica />

      <DispositivosConfianza />

      {/* Alertas de cobertura */}
      <section className={`rounded-2xl p-5 shadow-sm border ${alertas.datos?.incompletas ?? 0 > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-contorno-var'}`}>
        <h3 className="mb-2 flex items-center justify-between font-semibold text-neutro-800">
          ⚠ Cobertura a 48h
          {alertas.datos && alertas.datos.incompletas > 0 && (
            <span className="rounded-full bg-estado-error px-2 py-0.5 text-xs text-white">{alertas.datos.incompletas}</span>
          )}
        </h3>
        {alertas.cargando ? <Cargando texto="…" /> : alertas.error ? <ErrorApi mensaje={alertas.error} onReintentar={alertas.recargar} /> : alertas.datos && alertas.datos.incompletas > 0 ? (
          <ul className="space-y-1 text-sm">
            {alertas.datos.solicitudes.map((s) => (
              <li key={s.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5">
                <span>{formatFecha(s.fecha)} · {s.cliente_nombre} ({s.salon_nombre})</span>
                <span className="text-estado-error font-medium">{s.faltan} faltan</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-neutro-400">✓ Todo cubierto a 48h.</p>}
      </section>

      {/* No-show */}
      <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <h3 className="mb-2 font-semibold text-neutro-800">No-show (90 días)</h3>
        {noShow.cargando ? <Cargando texto="…" /> : noShow.datos && noShow.datos.total > 0 ? (
          <ul className="space-y-1 text-sm">
            {noShow.datos.personas.slice(0, 5).map((p) => (
              <li key={p.persona_id} className="flex items-center justify-between">
                <span>{p.apellido}, {p.nombre}</span>
                <span className={p.no_show_count >= 3 ? 'text-estado-error font-bold' : 'text-neutro-500'}>
                  {p.no_show_count} {p.no_show_count >= 3 ? '⚠' : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-neutro-400">✓ Sin no-shows en los últimos 90 días.</p>}
      </section>

      {/* Margen del mes */}
      <section className="rounded-2xl bg-primario-400 p-5 text-white shadow-sm">
        <h3 className="mb-2 font-semibold text-white/90">Margen del mes</h3>
        {margen.cargando ? <p className="text-white/70 text-sm">…</p> : margen.datos ? (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold">{formatU(margen.datos.total_cobro)}</p>
              <p className="text-xs text-white/70">Cobro</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{formatU(margen.datos.total_pago)}</p>
              <p className="text-xs text-white/70">Pago</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatU(margen.datos.margen)}</p>
              <p className="text-xs text-white/70">Margen</p>
            </div>
          </div>
        ) : null}
      </section>

      {/* Notificaciones push */}
      {notifPermiso === 'default' && (
        <section className="rounded-2xl bg-acento-500/10 p-4 ring-1 ring-acento-500/30">
          <p className="text-sm text-acento-600">🔔 Activá las notificaciones para recibir alertas de nuevas solicitudes.</p>
          <button className="mt-2 text-sm font-medium text-acento-600 underline" onClick={() => Notification.requestPermission()}>
            Activar
          </button>
        </section>
      )}

      {/* Accesos rápidos */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Link to="/admin/solicitudes" className="rounded-2xl bg-white p-4 text-center shadow-sm border border-contorno-var">
          <p className="text-sm text-neutro-500">Solicitudes</p>
          <p className="text-3xl">📋</p>
        </Link>
        <Link to="/admin/liquidaciones" className="rounded-2xl bg-white p-4 text-center shadow-sm border border-contorno-var">
          <p className="text-sm text-neutro-500">Dinero</p>
          <p className="text-3xl">$</p>
        </Link>
        <Link to="/admin/personal" className="rounded-2xl bg-white p-4 text-center shadow-sm border border-contorno-var">
          <p className="text-sm text-neutro-500">Personal</p>
          <p className="text-3xl">👥</p>
        </Link>
      </div>
    </div>
  );
}

interface DispositivoEmpresa {
  id: string;
  persona_id: string;
  device_id: string;
  modelo: string | null;
  estado: string;
  creado_en: string;
  resuelto_en: string | null;
  persona_nombre: string;
  persona_apellido: string;
}

function DispositivosConfianza(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<DispositivoEmpresa[]>('/api/dispositivos-confianza');
  const [verAprobados, setVerAprobados] = useState(false);
  const [resolviendo, setResolviendo] = useState<string | null>(null);

  async function resolver(id: string, accion: 'aprobar' | 'rechazar') {
    setResolviendo(id);
    try {
      await apiPost(`/api/dispositivos-confianza/${id}/resolver`, { accion });
      recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setResolviendo(null);
    }
  }

  async function desvincular(d: DispositivoEmpresa) {
    if (!confirmar(`¿Desvincular el dispositivo de ${d.persona_nombre} ${d.persona_apellido}? Va a tener que pedir aprobación de nuevo desde su celular.`)) return;
    setResolviendo(d.id);
    try {
      await apiPost(`/api/dispositivos-confianza/${d.id}`, undefined, 'DELETE');
      recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setResolviendo(null);
    }
  }

  const pendientes = (datos ?? []).filter((d) => d.estado === 'pendiente');
  const aprobados = (datos ?? []).filter((d) => d.estado === 'aprobado');

  return (
    <section className={`rounded-2xl p-5 shadow-sm border ${pendientes.length > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white border-contorno-var'}`}>
      <h3 className="mb-2 flex items-center justify-between font-semibold text-neutro-800">
        📱 Dispositivos de confianza
        {pendientes.length > 0 && (
          <span className="rounded-full bg-amber-600 px-2 py-0.5 text-xs text-white">{pendientes.length} por aprobar</span>
        )}
      </h3>

      {cargando ? <Cargando texto="…" /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : (
        <>
          {pendientes.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {pendientes.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 border border-contorno-var">
                  <div className="min-w-0">
                    <p className="font-medium">{d.persona_apellido}, {d.persona_nombre}</p>
                    <p className="text-xs text-neutro-400">{d.modelo ?? 'Dispositivo desconocido'} · pidió acceso el {formatFecha(d.creado_en)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg bg-estado-ok px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                      disabled={resolviendo === d.id}
                      onClick={() => void resolver(d.id, 'aprobar')}
                    >
                      ✓ Aprobar
                    </button>
                    <button
                      className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-estado-error disabled:opacity-50"
                      disabled={resolviendo === d.id}
                      onClick={() => void resolver(d.id, 'rechazar')}
                    >
                      ✕ Rechazar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutro-400">✓ Sin pedidos pendientes.</p>
          )}

          {aprobados.length > 0 && (
            <div className="mt-3">
              <button className="text-xs font-medium text-primario-400 underline hover:no-underline" onClick={() => setVerAprobados(!verAprobados)}>
                {verAprobados ? 'Ocultar aprobados' : `Ver aprobados (${aprobados.length})`}
              </button>
              {verAprobados && (
                <ul className="mt-2 space-y-1.5 text-sm">
                  {aprobados.map((d) => (
                    <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-superficie-100/60 px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-medium">{d.persona_apellido}, {d.persona_nombre}</p>
                        <p className="text-xs text-neutro-400">{d.modelo ?? 'Dispositivo desconocido'} · aprobado el {d.resuelto_en ? formatFecha(d.resuelto_en) : '—'}</p>
                      </div>
                      <button
                        className="text-xs font-semibold text-estado-error underline hover:no-underline disabled:opacity-50"
                        disabled={resolviendo === d.id}
                        onClick={() => void desvincular(d)}
                      >
                        Desvincular
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

type Config = { asignacion_automatica: number };

function SwitchAsignacionAutomatica(): ReactNode {
  const { datos, cargando, recargar } = useApi<Config>('/api/configuracion');
  const [guardando, setGuardando] = useState(false);

  async function alternar() {
    if (!datos || guardando) return;
    setGuardando(true);
    try {
      await apiPost('/api/configuracion', { asignacion_automatica: datos.asignacion_automatica !== 1 }, 'PUT');
      recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setGuardando(false);
    }
  }

  const activo = datos?.asignacion_automatica === 1;
  return (
    <section className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
      <div>
        <h3 className="font-semibold text-neutro-800">Asignación automática</h3>
        <p className="text-xs text-neutro-400">
          {activo
            ? 'Las convocatorias salen solas cuando el cliente crea o edita una solicitud.'
            : 'Vos lanzás cada asignación manualmente desde Solicitudes.'}
        </p>
      </div>
      <button
        role="switch"
        aria-checked={activo}
        disabled={cargando || guardando}
        onClick={alternar}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${activo ? 'bg-primario-400' : 'bg-neutro-300'} ${cargando || guardando ? 'opacity-50' : ''}`}
      >
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${activo ? 'left-[calc(100%-1.625rem)]' : 'left-0.5'}`} />
      </button>
    </section>
  );
}